import React, { useEffect, useState, useRef } from "react";
import { useDrawingStore } from "@/stores/drawingStore";
import {
  getDrawingViewportBounds,
  getDrawingSnapshot,
  setDrawingExtendData,
  removeDrawing,
} from "@/lib/lytick/overlay-bridge";

export function TextAnnotationEditor() {
  const editingId = useDrawingStore((s) => s.editingTextDrawingId);
  const setEditingTextDrawingId = useDrawingStore((s) => s.setEditingTextDrawingId);

  const [bounds, setBounds] = useState<any>(null);
  const [value, setValue] = useState("");
  const [extendData, setExtendData] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // 1. Sync viewport bounds to follow the chart position
  useEffect(() => {
    if (!editingId) {
      setBounds(null);
      setExtendData(null);
      setValue("");
      return;
    }

    const snapshot = getDrawingSnapshot(editingId);
    if (snapshot) {
      const ed = snapshot.extendData as any;
      setExtendData(ed);
      setValue(ed?.text || "");
    }

    const update = () => {
      const b = getDrawingViewportBounds(editingId);
      if (b) {
        setBounds(b);
      }
    };

    update();
    const interval = setInterval(update, 30);
    return () => clearInterval(interval);
  }, [editingId]);

  // 2. Auto-focus on mount
  useEffect(() => {
    if (bounds && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLTextAreaElement || inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [bounds]);

  if (!editingId || !bounds || !extendData) return null;

  const variant = extendData.variant || "box";
  const size = extendData.size || "M";
  const color = extendData.color || "#F9FAFB";
  const bold = !!extendData.bold;
  const italic = !!extendData.italic;
  const background = extendData.background || (variant === "tag" ? "rgba(148, 163, 184, 0.25)" : "rgba(30, 41, 59, 0.5)");

  const getFontSizePx = (s: string) => {
    switch (s) {
      case "S": return 12;
      case "M": return 14;
      case "L": return 18;
      case "XL": return 24;
      default: return 14;
    }
  };

  const fontSize = getFontSizePx(size);

  const handleCommit = (val: string) => {
    const trimmed = val.trim();
    if (!trimmed) {
      // If empty, clean up and remove
      removeDrawing(editingId);
    } else {
      setDrawingExtendData(editingId, {
        ...extendData,
        text: trimmed,
      });
    }
    setEditingTextDrawingId(null);
  };

  const handleCancel = () => {
    if (!extendData?.text) {
      removeDrawing(editingId);
    }
    setEditingTextDrawingId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    } else if (e.key === "Enter") {
      if (variant === "tag") {
        e.preventDefault();
        handleCommit(value);
      } else if (variant === "box" && !e.shiftKey) {
        e.preventDefault();
        handleCommit(value);
      }
    }
  };

  const handleBlur = () => {
    handleCommit(value);
  };

  // Common styling variables
  const fontStyle = `${italic ? "italic" : "normal"}`;
  const fontWeight = `${bold ? "bold" : "normal"}`;

  if (variant === "tag") {
    const editorWidth = Math.max(140, bounds.width + 24);
    const editorHeight = Math.max(34, bounds.height + 12);
    const left = bounds.centerX - editorWidth / 2;
    const top = bounds.centerY - editorHeight / 2;

    return (
      <div
        style={{
          position: "absolute",
          left,
          top,
          width: editorWidth,
          height: editorHeight,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="Tag name..."
          style={{
            width: "100%",
            height: "100%",
            padding: "6px 12px",
            backgroundColor: background,
            border: "1.5px solid var(--lyt-accent, #0EC4B4)",
            borderRadius: "6px",
            color,
            fontSize,
            fontFamily: "IBM Plex Sans, Inter, Roboto, sans-serif",
            fontStyle,
            fontWeight,
            textAlign: "center",
            outline: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5), 0 0 8px rgba(13,207,191,0.3)",
          }}
        />
      </div>
    );
  } else {
    // Box multi-line editor
    const editorWidth = Math.max(200, bounds.width + 32);
    const editorHeight = Math.max(90, bounds.height + 32);
    const left = bounds.left;
    const top = bounds.top;

    return (
      <div
        style={{
          position: "absolute",
          left,
          top,
          width: editorWidth,
          height: editorHeight,
          zIndex: 100,
        }}
      >
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="Type note..."
          style={{
            width: "100%",
            height: "100%",
            padding: "10px 12px",
            backgroundColor: background,
            border: "1.5px solid var(--lyt-accent, #0EC4B4)",
            borderRadius: "4px",
            color,
            fontSize,
            fontFamily: "IBM Plex Sans, Inter, Roboto, sans-serif",
            fontStyle,
            fontWeight,
            resize: "none",
            outline: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5), 0 0 8px rgba(13,207,191,0.3)",
            lineHeight: `${Math.max(fontSize + 4, 16)}px`,
          }}
        />
      </div>
    );
  }
}
